import type { UrlOpenMethod } from '../types/ChatWindow';
import { getUniqueChatWindowId } from './sessionStorageService';

enum EVENT_TYPES {
  UPDATE_WINDOW_TYPE = 'UPDATE_WINDOW_TYPE',
  NAVIGATE_TO_URL = 'NAVIGATE_TO_URL',
  SEND_CONSOLE_MESSAGE = 'SEND_CONSOLE_MESSAGE',
  LOCATION_REQUEST = 'LOCATION_REQUEST',
  RESTART_REQUEST = 'RESTART_REQUEST',
  TOGGLE_FULLSCREEN = 'TOGGLE_FULLSCREEN',
  SSO_AUTH_REQUEST = 'SSO_AUTH_REQUEST',
}

export type SSOAuthRequest = {
  requestId: string;
};

export type SSOAuthResponse = {
  requestId: string;
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
  idToken?: string;
  email?: string;
  upn?: string;
  cao?: string | null;
  error?: string;
};

export const sendUpdateWindowType = (windowType: string) => {
  window.parent.postMessage(
    { eventType: EVENT_TYPES.UPDATE_WINDOW_TYPE, payload: { windowType } },
    '*',
  );
};

export const sendNavigateToUrl = (url: string, openMethod?: UrlOpenMethod) => {
  window.parent.postMessage(
    { eventType: EVENT_TYPES.NAVIGATE_TO_URL, payload: { url, openMethod } },
    '*',
  );
};

export const sendLocationRequest = () => {
  window.parent.postMessage({ eventType: EVENT_TYPES.LOCATION_REQUEST }, '*');
};

export const sendLogMessage = (message: string, messageType: string) => {
  window.parent.postMessage(
    {
      eventType: EVENT_TYPES.SEND_CONSOLE_MESSAGE,
      payload: { message, messageType },
    },
    '*',
  );
};

export const sendRestartRequest = () => {
  window.parent.postMessage(
    {
      eventType: EVENT_TYPES.RESTART_REQUEST,
      requestSenderId: getUniqueChatWindowId(),
    },
    '*',
  );
};

export const sendToggleFullscreen = (isFullscreen: boolean) => {
  window.parent.postMessage(
    {
      eventType: EVENT_TYPES.TOGGLE_FULLSCREEN,
      payload: { isFullscreen },
    },
    '*',
  );
};

export const sendSSOAuthRequest = (): Promise<SSOAuthResponse> => {
  return new Promise((resolve, reject) => {
    const requestId = `sso_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const timeout = setTimeout(() => {
      cleanup();
      reject(
        new Error('SSO authentication timeout - parent window did not respond'),
      );
    }, 30000); // 30 second timeout

    const handleMessage = (event: MessageEvent) => {
      if (
        event.data?.eventType === 'CONVERSED_SSO_RESPONSE' &&
        event.data?.payload?.requestId === requestId
      ) {
        cleanup();
        const response = event.data.payload as SSOAuthResponse;
        if (response.success) {
          resolve(response);
        } else {
          reject(new Error(response.error || 'SSO authentication failed'));
        }
      }
    };

    const cleanup = () => {
      clearTimeout(timeout);
      window.removeEventListener('message', handleMessage);
    };

    window.addEventListener('message', handleMessage);

    window.parent.postMessage(
      {
        eventType: EVENT_TYPES.SSO_AUTH_REQUEST,
        payload: { requestId } as SSOAuthRequest,
      },
      '*',
    );
  });
};
